/**
 * TanStack Query hooks for managing invitations from the owner's
 * side of the flow: list, create, revoke.
 *
 * The public invitee-side hooks (inspect by token, accept by token)
 * live in `features/invitations/useInvitation.ts` because they
 * serve a different caller (the prospective invitee, not the
 * owner).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export type FamilyInvitationStatus =
  | "pending"
  | "accepted"
  | "revoked"
  | "expired";

export interface FamilyInvitation {
  id: string;
  family_id: string;
  invited_by_user_id: string;
  invite_email: string;
  status: FamilyInvitationStatus;
  token: string;
  invite_url: string;
  expires_at: string;
  created_at: string;
  accepted_at: string | null;
  accepted_by_user_id: string | null;
}

const FAMILY_INVITATIONS_QUERY_KEY = ["family", "invitations"] as const;

export function useFamilyInvitations() {
  return useQuery<FamilyInvitation[]>({
    queryKey: FAMILY_INVITATIONS_QUERY_KEY,
    queryFn: () => apiFetch<FamilyInvitation[]>("/api/families/mine/invitations"),
    staleTime: 30_000,
  });
}

export function useCreateFamilyInvitation() {
  const queryClient = useQueryClient();
  return useMutation<FamilyInvitation, Error, { invite_email: string }>({
    mutationFn: ({ invite_email }) =>
      apiFetch<FamilyInvitation>("/api/families/mine/invitations", {
        method: "POST",
        body: { invite_email },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FAMILY_INVITATIONS_QUERY_KEY });
    },
  });
}

export function useRevokeFamilyInvitation() {
  const queryClient = useQueryClient();
  return useMutation<FamilyInvitation, Error, { invitationId: string }>({
    mutationFn: ({ invitationId }) =>
      apiFetch<FamilyInvitation>(
        `/api/families/mine/invitations/${invitationId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FAMILY_INVITATIONS_QUERY_KEY });
    },
  });
}

export { FAMILY_INVITATIONS_QUERY_KEY };
